import { SummaryGrid, SummaryHeader, SummaryRow, SummarySection, SummaryText } from '../../../../components';
import { useFormContext } from 'react-hook-form';
import { claimFields } from '../../../../config';
import { useContext } from 'react';
import { Localization } from '../../../../config/localization/localization';
import { LanguageSelectorContext } from '../../../../context/LanguageSelectorContext';

import type { IClaim, IClaimInit } from '../../../../types';
import type { FC } from 'react';

interface IRepairDetailsProps {
    claim?: IClaimInit | IClaim | null;
    setStep: (step: number) => void;
}

export const RepairDetailsSummary: FC<IRepairDetailsProps> = ({ claim, setStep }) => {
    const formContext = useFormContext();
    const { selectedLanguage } = useContext(LanguageSelectorContext);
    const localization = new Localization(selectedLanguage);

    const isFreeFormRepairEntry: boolean | null = formContext.watch(claimFields.isFreeFormRepairEntry);

    // REPAIRS DONE ON SITE
    const wereRepairsDoneOnSite: string = formContext.getValues(claimFields.repairsDoneOnSite)
        ? localization.translate('dropdownOptions.yes')
        : localization.translate('dropdownOptions.no');
    const additionalCommentsLabel = isFreeFormRepairEntry
        ? localization.translate('repairDetailsSummary.additionalCommentsLabel')
        : localization.translate('repairDetailsSummary.repairDetailsLabel');
    const additionalComments: string =
        formContext.getValues(claimFields.claimComments)?.length !== 0
            ? formContext.getValues(claimFields.claimComments)
            : localization.translate('dropdownOptions.na');
    const legacyClaimNo: string =
        formContext.getValues(claimFields.legacyClaimNo)?.length !== 0
            ? formContext.getValues(claimFields.legacyClaimNo)
            : localization.translate('dropdownOptions.na');

    // CONSTRUCTION COORDINATOR
    const constructionCoordinatorName: string =
        formContext.getValues('constructionCoordinatorName')?.length !== 0
            ? formContext.getValues('constructionCoordinatorName')
            : localization.translate('dropdownOptions.na');
    const constructionCoordinatorPhone: string =
        formContext.getValues('constructionCoordinatorPhone')?.length !== 0
            ? formContext.getValues('constructionCoordinatorPhone')
            : localization.translate('dropdownOptions.na');
    const constructionCoordinatorEmail: string =
        formContext.getValues('constructionCoordinatorEmail')?.length !== 0
            ? formContext.getValues('constructionCoordinatorEmail')
            : localization.translate('dropdownOptions.na');

    // FUTURE REPAIRS
    const areFutureRepairsNeeded: string = formContext.getValues(claimFields.areFutureRepairsNeeded)
        ? localization.translate('dropdownOptions.yes')
        : localization.translate('dropdownOptions.no');
    const clientConstructionName =
        claim?.clientConstructionNumberAltName?.length !== 0
            ? claim?.clientConstructionNumberAltName
            : localization.translate('repairDetailsSummary.referenceLabel');
    const clientConstructionNumber: string =
        formContext.getValues(claimFields.clientConstructionNumber)?.length !== 0
            ? formContext.getValues(claimFields.clientConstructionNumber)
            : localization.translate('dropdownOptions.na');

    // Return to the repair details step
    const onStepClick = () => setStep(4);

    return (
        <div className={'flex flex-col'}>
            <SummaryHeader isLink onLink={onStepClick}>
                {localization.translate('repairDetailsSummary.stepLink')}
            </SummaryHeader>

            {/* REPAIRS */}
            <SummarySection>
                <SummaryGrid>
                    <SummaryRow hideColon label={localization.translate('repairDetailsSummary.repairsDoneOnSiteLabel')}>
                        {wereRepairsDoneOnSite}
                    </SummaryRow>
                    <SummaryRow label={localization.translate('repairDetailsSummary.referenceLabel')}>
                        {legacyClaimNo}
                    </SummaryRow>
                </SummaryGrid>
            </SummarySection>

            {/* FUTURE REPAIRS */}
            <SummarySection>
                <SummaryGrid>
                    <SummaryRow hideColon label={localization.translate('repairDetailsSummary.futureRepairsNeededLabel')}>
                        {areFutureRepairsNeeded}
                    </SummaryRow>
                    <SummaryRow label={clientConstructionName}>{clientConstructionNumber}</SummaryRow>
                    <SummaryRow label={localization.translate('repairDetailsSummary.constructionCoordinatorNameLabel')}>
                        {constructionCoordinatorName}
                    </SummaryRow>
                    <SummaryRow label={localization.translate('repairDetailsSummary.constructionCoordinatorEmailLabel')}>
                        {constructionCoordinatorEmail}
                    </SummaryRow>
                    <SummaryRow label={localization.translate('repairDetailsSummary.constructionCoordinatorPhoneLabel')}>
                        {constructionCoordinatorPhone}
                    </SummaryRow>
                    <SummaryText label={additionalCommentsLabel} value={additionalComments} />
                </SummaryGrid>
            </SummarySection>
        </div>
    );
};